import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, FormControlLabel, IconButton, Radio, RadioGroup, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { IProcessingGroupByBlock, IProcessingGroupBy } from './processingGroupBy.models';
import { ReactComponent as WarningIcon } from '../../../../src/assets/svg/warningIcon.svg';
import { AddPreprocessButton } from '../../../components/Buttons/addPreprocessButton';
import ProcessingSaveModal from '../processingSaveModal/processingSaveModal.component';
import { makeStyles } from '@mui/styles';
import { toast } from 'react-toastify';
import { dispatch } from '../../../store/hooks';
import { storageMiddleware } from '../../../store/slices/storage';
import { removeLastExtension } from '../../../helpers/removeExtension';
import { summaryMiddleware } from '../../../store/slices/summary';
import { modelVisualisationsMiddleware, modelVisualisationsSelector } from '../../../store/slices/modelVisualisations';
import API from '../../../manager/API';
import {
  actionsAutocompletePlaceholderStyles,
  columnsAutocompleteStyles,
  actionsOptionsParentStyles,
  blockStyles,
  borderStyles,
  boxStyles,
  buttonsStyles,
  categoricalColumnsHintStyles,
  closeIconStyles,
  columnsAutocompleteLiStyles,
  discardButtonEnableStyles,
  discardButtonStyles,
  extensionErrorStyles,
  formControlStyles,
  middleBlockStyles,
  newFileNameInputStyles,
  noCategoricalColumnsStyles,
  parentActionLabelStyles,
  parentActionRadioStyles,
  radiobuttonRowStyles,
  saveButtonStyles,
  scrollBlockStyles,
  titleBoxStyles,
  titleStyles,
  warningIconStyles,
  warningStyles,
  newFileNameSecondInputStyles
} from './processingGroupBy.styles';
import { processingGroupByEmptyBlock } from './processingGroupBy.constants';
import ProcessingGroupByInnerBlock from './processingGroupByInnerBlock/processingGroupByInnerBlock.component';
import { dataProcessingGroupByMiddleware } from '../../../store/slices/dataProcessingGroupBy';
import { dataProcessingGroupByStatusMiddleware } from '../../../store/slices/dataProcessingGroupByStatus';
import { dataProcessingGroupByProgressMiddleware } from '../../../store/slices/dataProcessingGroupByProgress';
const useStyles = makeStyles({
  root: {
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    scrollbarWidth: 'none', // Hide scrollbar in Firefox
    '-ms-overflow-style': 'none' // Hide scrollbar in IE and Edge
  }
});

const ProcessingGroupBy: React.FC<IProcessingGroupBy> = ({
  originalFileName,
  setData,
  open,
  onClose,
  processingGroupByBlocks,
  setProcessingGroupByBlocks,
  data
}) => {
  const classes = useStyles();
  const [isPlusDisabled, setIsPlusDisabled] = useState(false);
  const [fileNames, setFileNames] = useState({
    create: '',
    merge: ''
  });
  const [errors, setErrors] = useState<{
    create: { error: boolean; message: string };
    merge: { error: boolean; message: string };
  }>({
    create: { error: false, message: '' },
    merge: { error: false, message: '' }
  });
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState('');
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [saveProgress, setSaveProgress] = useState<number>(0);
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [resetCounter, setResetCounter] = useState(0);
  const [statusId, setStatusId] = useState<string>('');
  const [isStop, setIsStop] = useState<boolean>(false);
  const [stopped, setStopped] = useState<boolean>(false);
  const stoppedRef = React.useRef(stopped);

  const onlyCategoricalDropdownOptions = data
    ? Object.keys(data)
        .filter((key) => data[key].type === 'object')
        .map((key, index) => ({
          id: index,
          label: key
        }))
    : [];

  const onlyNumericDropdownOptions = data
    ? Object.keys(data)
        .filter((key) => data[key].type !== 'datetime64[ns]' && data[key].type !== 'object')
        .map((key, index) => ({
          id: index,
          label: key
        }))
    : [];

  const isDisabledButton =
    selectedOption.length > 0 ||
    fileNames.merge.length > 0 ||
    fileNames.create.length > 0 ||
    selectedColumns.length > 0 ||
    processingGroupByBlocks?.every((block: IProcessingGroupByBlock) => block.singleActionBody?.isCompleted === true);

  const isDisabled =
    processingGroupByBlocks?.every((block: IProcessingGroupByBlock) => block.singleActionBody?.isCompleted === true) &&
    selectedOption !== '' &&
    (fileNames.create !== '' || fileNames.merge !== '') &&
    !errors.merge.error &&
    selectedColumns.length > 0;

  const handleCloseModal = () => {
    setResetCounter((prev) => prev + 1);
    setModalOpen(false);
    setSaveProgress(0);
    setSaveStatus('');
    onClose();
    setSelectedColumns([]);
  };

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value as 'create' | 'merge');
  };

  const validateFileName = (value: string): { error: boolean; message: string } => {
    const validPattern = /^[a-zA-Z0-9_-]*$/;

    if (!validPattern.test(value)) {
      return { error: true, message: 'Spaces or special symbols are not allowed.' };
    }

    if (value.startsWith('-') || value.startsWith('_') || value.endsWith('-') || value.endsWith('_')) {
      return { error: true, message: 'The dataset name cannot start and end with a special character.' };
    }

    return { error: false, message: '' };
  };

  const handleFileNameChange = (mode: 'create' | 'merge') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFileNames((prev) => ({ ...prev, [mode]: value }));

    const validation = validateFileName(value);
    setErrors((prev) => ({
      ...prev,
      [mode]: {
        error: validation.error,
        message: validation.message
      }
    }));
  };

  const onPlusClick = () => {
    if (!processingGroupByBlocks.length) {
      setProcessingGroupByBlocks([{ id: 0, singleActionBody: {} }]);
    } else if (processingGroupByBlocks.length < 10) {
      setProcessingGroupByBlocks((prev: any) => [
        ...prev,
        { id: processingGroupByBlocks[processingGroupByBlocks.length - 1].id + 1, singleActionBody: {} }
      ]);
    }
  };

  const handleDiscard = () => {
    setProcessingGroupByBlocks([processingGroupByEmptyBlock({ id: 1 })]);
    setSelectedOption('');
    setFileNames({
      create: '',
      merge: ''
    });
    setSelectedColumns([]);
    setSaveProgress(0);
    setSaveStatus('');
    setResetCounter((prev) => prev + 1);
  };

  const steps = processingGroupByBlocks.map((block: IProcessingGroupByBlock) => ({
    aggregation_columns: block.singleActionBody?.aggregation_columns || [],
    aggregation_function: block.singleActionBody?.aggregation_function || []
  }));

  const handleSave = async () => {
    const params = {
      groupby_columns: selectedColumns,
      aggregation_steps: steps,
      output_dataset_name: `${fileNames.merge.length > 0 ? fileNames.merge : fileNames.create}.csv`,
      original_dataset_name: originalFileName,
      merge_with_original: selectedOption === 'merge'
    };
    setModalOpen(true);
    try {
      const dataProcessingGroupByUrl = '/data_operations/group_by';
      const response = await dispatch(
        dataProcessingGroupByMiddleware.dataProcessingGroupBy(params, dataProcessingGroupByUrl)
      );
      if (response?.status !== 200) {
        handleCloseModal();
      }
      // todop

      const saveData = response?.data;
      if (saveData && Object.keys(saveData).length > 0 && saveData?.status_update) {
        setStatusId(saveData?.status_update);
        const saveStatusUrl = `/data_operations/group_by/${saveData.status_update}`;
        const getSaveProgressURL = `/data_operations/group_by_progress/${saveData.status_update}`;
        let intervalId: any = setInterval(async () => {
          if (stoppedRef.current) {
            clearInterval(intervalId);
            return;
          }
          try {
            const saveStatusData = await dispatch(
              dataProcessingGroupByStatusMiddleware.dataProcessingGroupByStatus(saveStatusUrl)
            );
            const dataOfSaveProgress = await dispatch(
              dataProcessingGroupByProgressMiddleware.getDataProcessingGroupByProgress(getSaveProgressURL)
            );
            if (saveStatusData.status === 200) {
              if (
                dataOfSaveProgress &&
                dataOfSaveProgress.data.progress &&
                dataOfSaveProgress.data.progress.length > 0
              ) {
                setSaveProgress(dataOfSaveProgress.data.progress);
                setSaveStatus(dataOfSaveProgress.data.description);
              }

              if (saveStatusData.data.queue_state === 'SUCCESS') {
                const getStorageURL = `blob_file/get_user_storage_size`;
                dispatch(storageMiddleware.getStorage(getStorageURL));
                clearInterval(intervalId);
                handleCloseModal();
                onClose();
                toast.success(saveStatusData.data.status);
                const getPreviewUrl = `/data_operations/file_preview?filename=${
                  fileNames.merge.length > 0 ? fileNames.merge + '.csv' : fileNames.create + '.csv'
                }`;
                const getSummaryUrl = `/data_operations/data_summary?file_name=${
                  fileNames.merge.length > 0 ? fileNames.merge + '.csv' : fileNames.create + '.csv'
                }`;
                const fetchPreviewData = async () => {
                  try {
                    const response = await API.preview.getPreview(getPreviewUrl);
                    if (!response) {
                      throw new Error('Failed to fetch preview data');
                    }
                    if (response.data && response.data.file_id) {
                      const getVisualisationsUrl = `/visualisations/get_visualisations?id=${response.data.file_id}`;
                      dispatch(modelVisualisationsMiddleware.getModelVisualisations(getVisualisationsUrl));
                    }
                    setData(response.data);
                  } catch (error) {
                    console.error('Error fetching preview data:', error);
                  } finally {
                  }
                };

                fetchPreviewData();
                dispatch(summaryMiddleware.getSummary(getSummaryUrl));
              } else if (
                saveStatusData.data.queue_state === 'FAILURE' ||
                (saveStatusData && Object.keys(saveStatusData).length === 0)
              ) {
                handleCloseModal();
                clearInterval(intervalId);
                if (saveStatusData && Object.keys(saveStatusData).length === 0) {
                  handleCloseModal();
                  toast.error(removeLastExtension(saveStatusData.data.message));
                } else {
                  handleCloseModal();
                  toast.error(removeLastExtension(saveStatusData.data.message));
                }
              }
            } else {
              clearInterval(intervalId);
              toast.error(removeLastExtension(saveStatusData.data.message));
              handleCloseModal();
            }
          } catch (error: any) {
            clearInterval(intervalId);
            toast.error(removeLastExtension(error?.response.data.message));
            handleCloseModal();
          }
        }, 5000);
      }
    } catch (error: any) {
      console.error('Upload failed with error:', error?.message);
      toast.error(removeLastExtension(error?.response?.data?.message));
      handleCloseModal();
    }
  };

  const updateSingleActionBody = (
    blocks: IProcessingGroupByBlock[],
    targetBlockId: number,
    newSingleActionBody: any
  ) => {
    return blocks.map((block) => {
      if (block.id === targetBlockId) {
        return {
          ...block,
          singleActionBody: { ...newSingleActionBody } // Update the block's singleActionBody
        };
      }
      return block;
    });
  };

  const deleteBlock = (blockId: number) => {
    setProcessingGroupByBlocks((prevBlocks: IProcessingGroupByBlock[]) => {
      const updatedBlocks = prevBlocks.filter((block) => block.id !== blockId);
      return updatedBlocks; // Just return the filtered blocks without changing their ids
    });
  };

  const handleOptionsExhausted = (disabled: boolean) => {
    setIsPlusDisabled(disabled);
  };

  const handleColumnsChange = (_: any, newValue: Array<{ id: number; label: string }>) => {
    setSelectedColumns(newValue.map((option) => option.label));
  };

  useEffect(() => {
    if (!open) {
      handleDiscard();
    }
  }, [open]);

  useEffect(() => {
    if (selectedOption === 'merge') {
      setFileNames((prev) => ({
        ...prev,
        create: ''
      }));
    } else {
      setFileNames((prev) => ({
        ...prev,
        merge: ''
      }));
    }
  }, [selectedOption]);

  useEffect(() => {
    stoppedRef.current = stopped;
  }, [stopped]);

  return (
    <>
      <Box sx={boxStyles(open)}>
        <Box sx={titleBoxStyles}>
          <span style={titleStyles}>Groupby</span>
          <IconButton onClick={onClose} sx={closeIconStyles}>
            <CloseIcon />
          </IconButton>
        </Box>
        {onlyCategoricalDropdownOptions.length === 0 || onlyNumericDropdownOptions.length === 0 ? (
          <div style={warningStyles}>
            <IconButton style={warningIconStyles}>
              <WarningIcon />
            </IconButton>
            <span style={noCategoricalColumnsStyles}>
              {onlyCategoricalDropdownOptions.length === 0
                ? 'The dataset has no categorical columns: the Groupby functionality is not applicable'
                : 'The dataset has no numeric columns: the Groupby functionality is not applicable'}
            </span>
          </div>
        ) : (
          <>
            <div style={middleBlockStyles}>
              <div style={borderStyles}>
                <RadioGroup value={selectedOption} onChange={handleOptionChange} style={actionsOptionsParentStyles}>
                  <div style={radiobuttonRowStyles}>
                    <FormControlLabel
                      sx={formControlStyles}
                      value="create"
                      control={<Radio sx={parentActionRadioStyles} />}
                      label={<span style={parentActionLabelStyles}>Create new dataset with grouped results</span>}
                    />
                    {selectedOption === 'create' && (
                      <>
                        <input
                          style={newFileNameInputStyles}
                          onChange={handleFileNameChange('create')}
                          placeholder="New dataset name"
                          value={fileNames.create}
                        />
                        {errors.create.error && <p style={extensionErrorStyles}>{errors.create.message}</p>}
                      </>
                    )}
                  </div>
                  <div style={radiobuttonRowStyles}>
                    <FormControlLabel
                      sx={formControlStyles}
                      value="merge"
                      control={<Radio sx={parentActionRadioStyles} />}
                      label={<span style={parentActionLabelStyles}>Merge grouped results into original dataset</span>}
                    />
                    {selectedOption === 'merge' && (
                      <>
                        <input
                          style={newFileNameSecondInputStyles}
                          onChange={handleFileNameChange('merge')}
                          placeholder="New dataset name"
                          value={fileNames.merge}
                        />
                        {errors.merge.error && <p style={extensionErrorStyles}>{errors.merge.message}</p>}
                      </>
                    )}
                  </div>
                </RadioGroup>
              </div>
              <div style={blockStyles}>
                <Autocomplete
                  multiple
                  limitTags={4}
                  // value={onlyCategoricalDropdownOptions.filter((option: { id: number; label: string }) =>
                  //   selectedColumns.includes(option.label)
                  // )}
                  value={selectedColumns
                    .map((label) => onlyCategoricalDropdownOptions.find((option) => option.label === label)!)
                    .filter(Boolean)}
                  options={onlyCategoricalDropdownOptions.filter((option) => !selectedColumns.includes(option.label))}
                  onChange={handleColumnsChange}
                  disableCloseOnSelect={true}
                  sx={columnsAutocompleteStyles}
                  disableClearable={false}
                  renderOption={(props, option) => (
                    <li {...props} style={columnsAutocompleteLiStyles}>
                      {option.label}
                    </li>
                  )}
                  getOptionDisabled={() => selectedColumns.length >= 5}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select column"
                      label=""
                      InputProps={{
                        ...params.InputProps,
                        style: columnsAutocompleteLiStyles
                      }}
                      sx={actionsAutocompletePlaceholderStyles}
                    />
                  )}
                />
                <span style={categoricalColumnsHintStyles}>*select max 5 categorical columns to aggregate dataset</span>
                <div className={classes.root} style={scrollBlockStyles}>
                  {processingGroupByBlocks.map((block, index) => (
                    <div key={block.id}>
                      <ProcessingGroupByInnerBlock
                        resetCounter={resetCounter}
                        dropdownOptions={onlyNumericDropdownOptions}
                        updateSingleActionBody={updateSingleActionBody}
                        setBlocks={setProcessingGroupByBlocks}
                        block={block}
                        blocks={processingGroupByBlocks}
                        element={block.id}
                        deleteBlock={deleteBlock}
                        onOptionsExhausted={handleOptionsExhausted}
                        data={data}
                      />
                    </div>
                  ))}
                  {(processingGroupByBlocks.length <= 9 || onlyNumericDropdownOptions.length === 0) && (
                    <AddPreprocessButton
                      isGroupBy
                      convert={false}
                      processingGroupByBlocks={processingGroupByBlocks}
                      handlePlusButtonClick={onPlusClick}
                      title="Add an aggregation"
                      // isPlusDisabled={isPlusDisabled}
                    />
                  )}
                </div>
              </div>
            </div>

            <div style={buttonsStyles}>
              <button
                style={!isDisabledButton ? discardButtonStyles : discardButtonEnableStyles}
                onClick={handleDiscard}
              >
                Discard
              </button>
              <button onClick={handleSave} style={saveButtonStyles(isDisabled)} disabled={!isDisabled}>
                Save
              </button>
            </div>
          </>
        )}
      </Box>
      <ProcessingSaveModal
        progress={saveProgress}
        setProgress={setSaveProgress}
        uploadStatus={saveStatus}
        setStopped={setStopped}
        isStop={isStop}
        setIsStop={setIsStop}
        statusId={statusId}
        open={modalOpen}
        onClose={handleCloseModal}
        fileName={fileNames.merge.length > 0 ? fileNames.merge : fileNames.create}
        isGroupBy
      />
    </>
  );
};
export default ProcessingGroupBy;
